import { createEmptyPendingDependant } from '../OnboardingContext';

import type { IDependantDateOfBirth, IDependantFormValues } from './types';
import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IOnboardingDependant } from '../../../hooks/queries/useOnboardingDependants';
import type { DependantCount, IPendingDependant } from '../OnboardingContext';

/** Parse a `YYYY-MM-DD` ISO date back into an `IDependantDateOfBirth` triad. */
export const parseIsoToDateOfBirth = (iso: string | null): IDependantDateOfBirth | null => {
  if (!iso) {
    return null;
  }
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
};

/**
 * Convert a persisted dependant row into form-values shape. Used to seed
 * pending state on resume (count step) and to render edit forms (entry step)
 * — both paths need the same lossless server → form mapping.
 */
export const dependantToFormValues = (dep: {
  relationship: CaregiverRelationshipKind;
  isStep: boolean;
  isInLaw: boolean;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: string | null;
}): IDependantFormValues => ({
  relationship: dep.relationship,
  isStep: dep.isStep,
  isInLaw: dep.isInLaw,
  firstName: dep.firstName,
  lastName: dep.lastName,
  preferredName: dep.preferredName ?? '',
  dateOfBirth: parseIsoToDateOfBirth(dep.dateOfBirth),
});

/**
 * Build `count` pending slots, seeding the first `count` from existing server
 * dependants (carrying their `serverId` / `caregiverRelationshipId` so the
 * review step takes the update path, not a duplicate create) and padding any
 * remainder with empty slots. Used to hydrate `pendingDependants` on entry to
 * the loved-ones flow from any direction — forward, resume, or back-nav.
 */
export const seedPendingDependantsFromServer = (
  dependants: ReadonlyArray<IOnboardingDependant>,
  count: DependantCount
): IPendingDependant[] =>
  Array.from({ length: count }, (_, i) => {
    const dep = dependants[i];
    if (!dep) {
      return createEmptyPendingDependant();
    }
    return {
      values: dependantToFormValues(dep),
      serverId: dep.id,
      caregiverRelationshipId: dep.caregiverRelationshipId,
    };
  });

/** Build a `YYYY-MM-DD` ISO date string from a captured `IDependantDateOfBirth`. */
export const toIsoDate = (dob: IDependantDateOfBirth | null): string | null => {
  if (!dob) {
    return null;
  }
  const mm = String(dob.month).padStart(2, '0');
  const dd = String(dob.day).padStart(2, '0');
  return `${dob.year}-${mm}-${dd}`;
};

/**
 * Grow or shrink an array to length `count`. Used when the count step changes
 * how many dependants are being collected — preserve filled-out forms when
 * possible, fill new slots with `fill()`.
 */
export const truncateForCount = <T>(arr: T[], count: number, fill: () => T): T[] => {
  if (arr.length === count) {
    return arr;
  }
  if (arr.length > count) {
    return arr.slice(0, count);
  }
  const next = [...arr];
  while (next.length < count) {
    next.push(fill());
  }
  return next;
};

/**
 * Pick the dependant's display name — preferred name when set, else first
 * name. Last name is intentionally omitted across the loved-ones surface; we
 * lean on the more familiar form (preferred / first) everywhere.
 */
export const displayName = (dep: { firstName: string; preferredName?: string | null }): string => {
  const preferred = dep.preferredName?.trim();
  if (preferred) {
    return preferred;
  }
  return dep.firstName.trim();
};

/**
 * "Review details for X and Y" title — falls back when names are missing.
 * Accepts a structural shape so both pending-form values (`IDependantFormValues`)
 * and persisted server rows (`IOnboardingDependant`) flow through one helper.
 */
export const buildReviewTitle = (
  dependants: ReadonlyArray<{ firstName: string; preferredName?: string | null }>
): string => {
  if (dependants.length === 0) {
    return 'Review your loved ones';
  }
  const names = dependants.map(displayName).filter((n) => n.length > 0);
  if (names.length === 0) {
    return 'Review your loved ones';
  }
  if (names.length === 1) {
    return `Review details for ${names[0]}`;
  }
  return `Review details for ${names[0]} and ${names[1]}`;
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  MOTHER: 'Mother',
  FATHER: 'Father',
  GRANDMOTHER: 'Grandmother',
  GRANDFATHER: 'Grandfather',
  AUNT: 'Aunt',
  UNCLE: 'Uncle',
  OTHER: 'Other',
};

/**
 * Human label for a caregiver relationship + step/in-law modifiers. Accepts a
 * structural shape so both pending-form values and persisted server rows
 * (where the relationship is non-nullable) flow through one helper.
 */
export const formatRelationship = (dep: { relationship: string | null; isStep: boolean; isInLaw: boolean }): string => {
  if (!dep.relationship) {
    return '—';
  }
  const base = RELATIONSHIP_LABELS[dep.relationship] ?? dep.relationship;
  const modifiers: string[] = [];
  if (dep.isStep) {
    modifiers.push('step');
  }
  if (dep.isInLaw) {
    modifiers.push('in law');
  }
  return modifiers.length === 0 ? base : `${base} ${modifiers.join(' ')}`;
};
